import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import StepDocumentSection from 'features/onboarding/components/upload/StepDocumentSection';
import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useRegisterStepNav } from 'features/onboarding/context/StepNavContext';

/**
 * Payment step — documents only. The partner uploads Osek, tax clearance and
 * bank confirmation; OCR still prefills taxLegalInfo / bankAccountInfo in the
 * background. Fields are reviewed and edited on the summary step.
 */
const StepPayment: React.FC = () => {
  const { t } = useTranslation();
  const { data, goNext, goBack, registerStepGuard } = useOnboarding();
  const [docError, setDocError] = useState<string | undefined>();

  const validateDocs = (): boolean => {
    if (
      !data.documents.osekCertificate ||
      !data.documents.taxClearance ||
      !data.documents.bankConfirmation
    ) {
      setDocError('documentRequired');
      return false;
    }
    setDocError(undefined);
    return true;
  };

  const handleNext = () => {
    if (!validateDocs()) return;
    goNext();
  };

  useRegisterStepNav({
    onBack: goBack,
    onNext: handleNext,
    nextLabel: t('nav.payment') as string,
  });

  const validateRef = useRef(validateDocs);
  validateRef.current = validateDocs;

  useEffect(() => {
    registerStepGuard(() => validateRef.current());
    return () => registerStepGuard(null);
  }, [registerStepGuard]);

  return (
    <StepCard
      title={t('payment.title')}
      subtitle={t('payment.subtitle') as string}
    >
      <div className="mb-4">
        <StepDocumentSection
          docKey="osekCertificate"
          ocr
          required
          error={data.documents.osekCertificate ? undefined : docError}
        />
      </div>
      <div className="mb-4">
        <StepDocumentSection
          docKey="taxClearance"
          required
          error={data.documents.taxClearance ? undefined : docError}
        />
      </div>

      <div className="mb-4 mt-2">
        <h3 className="text-base font-semibold text-gray-900">
          {t('payment.bankHeading')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 leading-relaxed">
          {t('payment.bankSubheading')}
        </p>
      </div>

      <div className="mb-4">
        <StepDocumentSection
          docKey="bankConfirmation"
          ocr
          required
          error={data.documents.bankConfirmation ? undefined : docError}
        />
      </div>

      <p className="mt-2 text-sm text-gray-600 leading-relaxed rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3">
        {t('payment.reviewNote')}
      </p>
    </StepCard>
  );
};

export default StepPayment;
